import { useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { Tooltip } from "@heroui/react";
import {
  ArrowSquareOut,
  ArrowsClockwise,
  CircleNotch,
  CloudArrowUp,
} from "@phosphor-icons/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";

interface OdooContractActionProps {
  contract: { id: number; odooContractId?: number | null };
  /** Called after a successful sync so the table can pick up the new Odoo id */
  onSynced?: () => void;
  size?: number;
}

interface OdooStatus {
  odooContractId: number | null;
  existsInOdoo: boolean;
  orderName: string | null;
  state: string | null;
  odooCompanyName: string | null;
  matchesContractCompany: boolean | null;
}

const odooContractUrl = (odooContractId: number) =>
  `https://portal.yally.be/web#id=${odooContractId}&cids=1%2C2%2C3%2C4&menu_id=369&action=1217&model=sale.order&view_type=form`;

/**
 * Odoo actions for one contract: open the order, and (re)create it in Odoo.
 *
 * The resync is available even when the contract already has an odooContractId — that id is
 * no proof the order is still there or correct. It may have been deleted in Odoo, or belong
 * to the wrong company. So the confirmation first asks Odoo what is actually at that id and
 * words itself accordingly, instead of blindly warning about "the existing order".
 */
export const OdooContractAction = ({ contract, onSynced, size = 22 }: OdooContractActionProps) => {
  const { session } = useSession();
  const [isSyncing, setIsSyncing] = useState(false);

  const fetchOdooStatus = async (): Promise<OdooStatus | null> => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/${contract.id}/odoo-status`,
        { method: "GET" },
        session,
      );
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error("Could not read Odoo status:", error);
      return null;
    }
  };

  const buildConfirmMessage = (status: OdooStatus | null): string => {
    if (!contract.odooContractId) {
      return "Dit contract bestaat nog niet in Odoo. Wil je het nu aanmaken voor de vennootschap van dit gebouw?";
    }

    if (!status) {
      return (
        `Dit contract verwijst naar Odoo-order ${contract.odooContractId}, maar de status in Odoo kon niet ` +
        `gecontroleerd worden.\n\nOpnieuw aanmaken maakt een NIEUWE order en koppelt die aan dit contract. ` +
        `Bestaat de oude order nog, dan blijft die in Odoo staan en moet je hem daar verwijderen.\n\nDoorgaan?`
      );
    }

    if (!status.existsInOdoo) {
      return (
        `De order waarnaar dit contract verwijst (${status.odooContractId}) bestaat niet meer in Odoo.\n\n` +
        `Het contract wordt opnieuw aangemaakt en de verwijzing wordt vervangen. Doorgaan?`
      );
    }

    const wrongCompany = status.matchesContractCompany === false;

    return (
      `Dit contract is gekoppeld aan Odoo-order ${status.orderName || status.odooContractId}` +
      `${status.state ? ` (status ${status.state})` : ""}` +
      `${status.odooCompanyName ? ` van ${status.odooCompanyName}` : ""}.` +
      (wrongCompany
        ? `\n\nLet op: die order staat op een ANDERE vennootschap dan dit gebouw.`
        : "") +
      `\n\nOpnieuw aanmaken maakt een NIEUWE order voor de vennootschap van dit gebouw en koppelt die aan ` +
      `het contract, inclusief alle contractlijnen. De oude order blijft in Odoo staan en moet je daar ` +
      `zelf verwijderen.\n\nDoorgaan?`
    );
  };

  const handleSync = async () => {
    if (isSyncing) return;

    setIsSyncing(true);
    try {
      // Only worth asking Odoo when there is a reference to verify.
      const status = contract.odooContractId ? await fetchOdooStatus() : null;

      if (!window.confirm(buildConfirmMessage(status))) {
        return;
      }

      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/${contract.id}/create-in-odoo`,
        { method: "POST" },
        session,
      );

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.message || `Aanmaken in Odoo mislukt (${response.status})`);
      }

      const result = await response.json();
      toast.success(
        `Contract aangemaakt in Odoo (order ${result.odooContractId}, ${result.orderLineCount} lijn${
          result.orderLineCount === 1 ? "" : "en"
        })`,
      );

      if (result.replacedOdooContractId) {
        toast.info(
          `De vorige Odoo-order (${result.replacedOdooContractId}) is losgekoppeld. Bestaat die nog in Odoo, verwijder hem daar.`,
          { autoClose: 10000 },
        );
      }

      onSynced?.();
    } catch (error: any) {
      console.error("Error creating contract in Odoo:", error);
      toast.error(error?.message || "Contract kon niet in Odoo aangemaakt worden", { autoClose: 10000 });
      Sentry.captureException(error, {
        tags: { feature: "contractOdooSync", method: "createInOdoo" },
        extra: { contractId: contract.id, odooContractId: contract.odooContractId },
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const syncIcon = isSyncing ? (
    <CircleNotch weight="bold" size={size} className="animate-spin" />
  ) : contract.odooContractId ? (
    <ArrowsClockwise weight="duotone" size={size} />
  ) : (
    <CloudArrowUp weight="duotone" size={size} />
  );

  return (
    <div className="flex items-center gap-3">
      {contract.odooContractId && (
        <Tooltip content="In Odoo openen">
          <a
            className="text-moss-primary"
            href={odooContractUrl(contract.odooContractId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ArrowSquareOut weight="duotone" size={size} />
          </a>
        </Tooltip>
      )}

      <Tooltip
        content={
          contract.odooContractId
            ? "Opnieuw aanmaken in Odoo (vervangt de huidige order)"
            : "Bestaat niet in Odoo — nu aanmaken"
        }
      >
        <a
          className={`cursor-pointer ${contract.odooContractId ? "text-neutral-secondary-mid" : "text-brick-primary"}`}
          onClick={handleSync}
        >
          {syncIcon}
        </a>
      </Tooltip>
    </div>
  );
};

export default OdooContractAction;
